import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { errorMessage } from '@/lib/api/client';
import { joinAffiliate, submitAffiliateApplication } from '@/api/marketing.api';
import { Seo } from '@/lib/seo/Seo';
import { useAuth } from '@/features/auth/AuthContext';
import { useUI } from '@/app/providers/UIContext';
import { isAffiliateRole, staffPath } from '@/pages/admin/lib/staffPanelPath';
import {
  AUTH_EYE,
  AUTH_FIELD,
  AUTH_LINK,
  AUTH_PRIMARY,
  AuthField,
  AuthShell,
  isStrongPassword,
} from '@/pages/public/auth/AuthShell';

export default function AffiliateRegisterPage() {
  const { t } = useTranslation();
  const { user, loading, signup, logout } = useAuth();
  const { toast } = useUI();
  const navigate = useNavigate();

  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (!loading && user && isAffiliateRole(user.role)) {
    return <Navigate to={staffPath(user.role, 'overview')} replace />;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError(t('affiliate.register_modal.err_required'));
      return;
    }
    if (!user) {
      if (!password) {
        setError(t('affiliate.register_modal.err_password_required'));
        return;
      }
      if (!isStrongPassword(password)) {
        setError(t('affiliate.register_modal.err_password_weak'));
        return;
      }
    }
    setBusy(true);
    try {
      if (!user) {
        await signup(name.trim(), email.trim(), password);
      }
      await submitAffiliateApplication({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
      });
      try {
        await joinAffiliate();
      } catch {
        // Application still recorded even if join requires extra steps
      }
      setSent(true);
      toast(t('affiliate.register_modal.toast'));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Seo
        title="Affiliate registration"
        path="/affiliate/register"
        desc="Register as a MAKTech Academy affiliate partner and earn commission."
      />
      <AuthShell
        title={sent ? t('affiliate.register_modal.sent_title') : 'Affiliate register'}
        backTo="/affiliate"
        footer={
          sent ? null : (
            <>
              Already registered?{' '}
              <Link to="/login" className={AUTH_LINK}>
                Log in
              </Link>
            </>
          )
        }
      >
        {!loading && user ? (
          <div className="text-center" data-testid="affiliate-register-logout-required">
            <p className="mb-5 text-[14px] leading-[1.65] text-white/85">
              Affiliate registration needs a logged-out state. Please log out first, then register.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2.5">
              <button
                type="button"
                className={AUTH_PRIMARY}
                data-testid="affiliate-reg-logout"
                onClick={() => void logout()}
              >
                Log out now
              </button>
              <Link to="/affiliate" className={AUTH_LINK}>
                Back to affiliate page
              </Link>
            </div>
          </div>
        ) : sent ? (
          <div className="text-center" data-testid="affiliate-register-sent">
            <p className="mb-5 text-[14px] leading-[1.65] text-white/85">{t('affiliate.register_modal.sent_body')}</p>
            <button
              type="button"
              className={AUTH_PRIMARY}
              onClick={() => navigate('/affiliate')}
              data-testid="affiliate-reg-done"
            >
              {t('affiliate.register_modal.close')}
            </button>
          </div>
        ) : (
          <form onSubmit={(e) => void submit(e)} data-testid="affiliate-register-form">
            <AuthField label={t('affiliate.register_modal.name')} htmlFor="aff-reg-name">
              <input
                id="aff-reg-name"
                className={AUTH_FIELD}
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoComplete="name"
                placeholder="Your name"
                required
                data-testid="affiliate-reg-name"
              />
            </AuthField>
            <AuthField label={t('affiliate.register_modal.email')} htmlFor="aff-reg-email">
              <input
                id="aff-reg-email"
                className={AUTH_FIELD}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="username@gmail.com"
                required
                data-testid="affiliate-reg-email"
              />
            </AuthField>
            <AuthField label={t('affiliate.register_modal.phone')} htmlFor="aff-reg-phone">
              <input
                id="aff-reg-phone"
                className={AUTH_FIELD}
                placeholder="01X-XXXXXXXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                autoComplete="tel"
                required
                data-testid="affiliate-reg-phone"
              />
            </AuthField>
            {!user ? (
              <AuthField label={t('affiliate.register_modal.password')} htmlFor="aff-reg-password">
                <div className="relative">
                  <input
                    id="aff-reg-password"
                    className={`${AUTH_FIELD} pr-11`}
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('affiliate.register_modal.password_placeholder')}
                    autoComplete="new-password"
                    required
                    data-testid="affiliate-reg-password"
                  />
                  <button
                    type="button"
                    className={AUTH_EYE}
                    aria-label={
                      showPassword
                        ? t('affiliate.register_modal.hide_password')
                        : t('affiliate.register_modal.show_password')
                    }
                    data-testid="affiliate-reg-password-toggle"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? <FiEyeOff className="size-4" aria-hidden /> : <FiEye className="size-4" aria-hidden />}
                  </button>
                </div>
                <span className="mt-1.5 block text-xs text-white/65">
                  {t('affiliate.register_modal.password_hint')}
                </span>
              </AuthField>
            ) : null}

            {error ? (
              <p className="mb-3 text-[13px] text-[#ffe0d6]" data-testid="affiliate-reg-error">
                {error}
              </p>
            ) : null}

            <button type="submit" className={AUTH_PRIMARY} disabled={busy} data-testid="affiliate-reg-submit">
              {busy ? t('affiliate.register_modal.submitting') : t('affiliate.register_modal.submit')}
            </button>
          </form>
        )}
      </AuthShell>
    </>
  );
}
